package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.CanvasMentionDto
import dev.lingvo.shared.CanvasReviewDto
import dev.lingvo.shared.CreateCanvasCommentRequest
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.PutCanvasSlotMarkRequest
import dev.lingvo.shared.RenderDto
import dev.lingvo.shared.ScreenshotSlotDto
import dev.lingvo.shared.SetCanvasThreadStateRequest
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.api.screenshotRenders
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private data class CanvasReviewData(val review: CanvasReviewDto, val members: List<CanvasMentionDto>, val renders: List<RenderDto>)

/** Both the canvas and set review read the same saved-version discussion; unsaved geometry cannot acquire consent. */
@Composable
internal fun CanvasReviewPanel(
    slug: String, platform: StorePlatform, version: Int, slots: List<ScreenshotSlotDto>, device: DeviceClass,
    locale: String, selectedSlot: String? = null, selectedLayer: String? = null, saved: Boolean = true,
    canComment: Boolean, canMark: Boolean, onSelectSlot: (String) -> Unit = {}, refreshEpoch: Int = 0,
) {
    if (version <= 0 || slots.isEmpty()) return
    if (!saved) {
        LMuted(t("app.projects.slug.screenshots.canvas_review_panel.save_the_design_before_commenting_on", "Save the design before commenting on this version or marking a screenshot."))
        return
    }
    val subject = liveProjectRoute(slug)?.authoritySubject(Session.organizations)
    var reload by remember(subject, platform, version) { mutableStateOf(0) }
    LaunchedEffect(refreshEpoch) { if (refreshEpoch != 0) reload++ }
    val loaded = loadReloadable(subject, platform, version, reloadToken = reload) {
        val action = subject?.action ?: awaitCancellation()
        requestProjectResource(action, slug) {
            CanvasReviewData(ScreenshotReviewApi.review(slug, platform, version), ScreenshotReviewApi.members(slug, platform, version),
                ApiClient.screenshotRenders(slug, platform))
        }
    }
    Column(Modifier.fillMaxWidth().gap(Space.MD).styleModifier { property("min-width", "0"); property("overflow-wrap", "anywhere") }) {
        H3 { Text(t("app.projects.slug.screenshots.canvas_review_panel.canvas_discussion_and_screenshot_marks", "Canvas discussion and screenshot marks")) }
        LButton(t("app.projects.slug.screenshots.canvas_review_panel.refresh_discussion", "Refresh discussion"), { reload++ }, variant = LButtonVariant.SECONDARY)
        loaded.refreshError?.let { AsyncFailureState(it, subject = t("app.projects.slug.screenshots.canvas_review_panel.the_canvas_discussion", "the canvas discussion"), onRetry = { reload++ }) }
        when (val state = loaded.state) {
            Async.Loading -> LoadingState(rows = 2)
            is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.canvas_review_panel.the_canvas_discussion", "the canvas discussion"), onRetry = { reload++ })
            is Async.Success -> key(subject, platform, version) {
                CanvasReviewContent(slug, platform, version, slots, device, locale, selectedSlot, selectedLayer, state.value,
                    canComment, canMark, loaded.refreshing || loaded.refreshError != null, onSelectSlot, reload) { reload++ }
            }
        }
    }
}

@Composable
private fun CanvasReviewContent(
    slug: String, platform: StorePlatform, version: Int, slots: List<ScreenshotSlotDto>, device: DeviceClass,
    locale: String, selectedSlot: String?, selectedLayer: String?, data: CanvasReviewData,
    canComment: Boolean, canMark: Boolean, validating: Boolean, onSelectSlot: (String) -> Unit, refreshEpoch: Int, onReload: () -> Unit,
) {
    var slotId by remember(selectedSlot, slots) { mutableStateOf(selectedSlot?.takeIf { id -> slots.any { it.id == id } } ?: slots.first().id) }
    var pinLayer by remember(selectedLayer) { mutableStateOf(selectedLayer != null) }
    val anchor = CanvasReviewAnchor(version, slotId, locale, selectedLayer?.takeIf { pinLayer })
    val slot = slots.first { it.id == slotId }
    val scope = rememberCoroutineScope()
    val identity = Session.requestIdentity
    val runner = remember(slug, platform, identity) { ScreenshotsActionRunner(slug, { scope.launch { it() } }) }
    var busy by remember(anchor, identity) { mutableStateOf(false) }
    var failure by remember(anchor, identity) { mutableStateOf<String?>(null) }
    val person = Session.isAuthenticated && Session.token?.split('.')?.size == 3
    val writable = canComment && person && !validating && !busy
    fun failed(error: Throwable) {
        failure = tr("app.projects.slug.screenshots.canvas_review_panel.the_review_could_not_be_updated", "The review could not be updated. Refresh to check current access and content, then retry.")
        onReload()
    }
    Div(Modifier.styleModifier { property("min-width", "0") }.toAttrs { attr("data-canvas-review", ""); attr("aria-live", "polite") }) {
        LMuted(tf("screenshots.collaboration.version_summary", "Version {version}: {open} open discussions, {approved} approval marks, {changes} change requests, {stale} outdated marks.",
            "version" to version, "open" to data.review.summary.openThreads, "approved" to data.review.summary.approvedMarks,
            "changes" to data.review.summary.changesRequestedMarks, "stale" to data.review.summary.staleMarks))
        LMuted(t("app.projects.slug.screenshots.canvas_review_panel.screenshot_marks_inform_the_persons_set", "Screenshot marks inform the person's set approval. Review and approve the complete set separately before pushing."))
        LSelect(slotId, slots.map { LSelectOption(it.id, it.title) }, { slotId = it; onSelectSlot(it) },
            label = t("app.projects.slug.screenshots.canvas_review_panel.discussion_screenshot", "Discussion screenshot"), enabled = !busy,
            modifier = Modifier.fillMaxWidth().styleModifier { property("min-width", "0") })
        LMuted(tf("screenshots.collaboration.anchor", "Pinned to {shot} · {language} · version {version}",
            "shot" to slot.title, "language" to localeInfo(locale).englishName, "version" to version))
        if (selectedLayer != null) LCheckbox(pinLayer, { pinLayer = it },
            t("app.projects.slug.screenshots.canvas_review_panel.pin_to_selected_canvas_layer", "Pin to selected canvas layer"), enabled = !busy)
        if (!person) LMuted(t("app.projects.slug.screenshots.canvas_review_panel.sign_in_as_a_person_to", "Sign in as a person to comment or mark a screenshot."))
        failure?.let { LAlert(it, variant = LAlertVariant.WARNING, live = true) }
        TagElement<HTMLElement>("details", applyAttrs = {}) {
            TagElement<HTMLElement>("summary", applyAttrs = { style { property("min-height", "40px"); property("padding-block", "8px"); property("box-sizing", "border-box") } }) {
                Text(t("app.projects.slug.screenshots.canvas_review_panel.view_comments_and_add_feedback", "View comments and add feedback"))
            }
            key(anchor) {
                CanvasReviewThreads(data.review.threads.filter {
                    canvasReviewAnchorMatches(anchor, it.designVersion, it.slotId, it.locale, it.layerId)
                }, data.members, writable, busy,
                    onPost = { parent, text, mentions, done ->
                        val request = CreateCanvasCommentRequest(slotId = anchor.slotId, locale = anchor.locale,
                            layerId = if (parent != null) parent.layerId else anchor.layerId, body = text, mentions = mentions, parentId = parent?.id)
                        runner.run(ScreenshotsPrivilege.COMMENT, { busy = it }, {
                            ScreenshotReviewApi.comment(slug, platform, version, request)
                        }, ::failed, { failure = null; done(); onReload() })
                    }, onState = { thread ->
                        runner.run(ScreenshotsPrivilege.COMMENT, { busy = it }, {
                            ScreenshotReviewApi.state(slug, platform, version, thread.id, SetCanvasThreadStateRequest(thread.revision, !thread.resolved))
                        }, ::failed, { failure = null; onReload() })
                    })
            }
        }
        val render = data.renders.firstOrNull {
            canvasReviewRenderMatches(anchor, device, it.designVersion, it.slotId, it.locale, it.deviceClass, true)
        }
        CanvasMarkPreview(slug, platform, data.review.marks.filter { it.slotId == slotId && it.locale == locale && it.deviceClass == device },
            render, canMark && person && !validating && !busy, busy, refreshEpoch) { state ->
            if (render != null) {
                val own = data.review.marks.firstOrNull { it.slotId == slotId && it.locale == locale && it.deviceClass == device && it.reviewerId == Session.user?.id }
                runner.run(ScreenshotsPrivilege.APPROVE, { busy = it }, {
                    ScreenshotReviewApi.mark(slug, platform, version, PutCanvasSlotMarkRequest(slotId = render.slotId, locale = render.locale,
                        deviceClass = render.deviceClass, renderId = render.id, sha256 = render.sha256, state = state, baseRevision = own?.revision))
                }, ::failed, { failure = null; onReload() })
            }
        }
    }
}
